/**
 * Where Home's rows, the hero, Live Now's fill, the history rails' pictures,
 * title details and title matching come from (Section 26; ADR "Home rows and
 * title pages from open data"): **TMDB**, today's code, or **Open**, the
 * provider's own data plus the public discovery files. Screens, tiles and
 * remote behaviour are the same for both; only the lists behind them differ.
 *
 * Every TMDB use asks `usesTmdb()` and takes its Open path when it says no.
 *
 * - **Every build made without a TMDB key: Open** (Section 26 step 6, owner, 2026-10-12). Release
 *   builds and the website carry none, so "no key" is the one fact that decides it: nothing can
 *   call TMDB, and every Open path is in place (the APK natively, the website in the page:
 *   discovery/webDiscovery.ts). The website's server key was removed the same day.
 * - **A test build** keeps the key, so it starts on TMDB and the switch below flips it.
 * - **Test builds only** (`VITE_TEST_BUILD`, set by scripts/android-test-build.sh):
 *   a hidden switch for comparing the two on the box. Set it through remote
 *   debugging (scripts/lib/debugClient.mjs) and reload:
 *     localStorage.setItem("slq.rowsFrom", "open"); location.reload()
 *   ("tmdb", or removing the key, goes back). Release builds ignore it, so no
 *   setting can turn TMDB on or off in a viewer's app.
 */

export type RowSupplier = "tmdb" | "open";

export const ROW_SUPPLIER_KEY = "slq.rowsFrom";

/** A build made by scripts/android-test-build.sh. The `?.` is for the test runner, which has no import.meta.env. */
export function isTestBuild(): boolean {
  return import.meta.env?.VITE_TEST_BUILD === "1";
}

let override: RowSupplier | null | undefined;

/** The key this build was made with. Release builds are made without one; test builds keep it. */
let buildKey: string | undefined | null = null;

function switchSetting(): RowSupplier | null {
  if (!isTestBuild()) return null;
  try {
    const value = localStorage.getItem(ROW_SUPPLIER_KEY);
    return value === "open" || value === "tmdb" ? value : null;
  } catch {
    return null;
  }
}

/** Read once per page load: the switch takes effect on reload, so a screen never mixes the two. */
export function rowSupplier(): RowSupplier {
  if (override !== undefined && override !== null) return override;
  if (override === undefined) override = switchSetting();
  if (override) return override;
  return defaultSupplier();
}

/** What a build without a switch uses: TMDB only while the build itself carries a key. */
function defaultSupplier(): RowSupplier {
  // Tests state the build they stand for; the test runner has no build environment at all, and
  // keeps the code's original default there.
  if (buildKey !== null) return buildKey ? "tmdb" : "open";
  const env = import.meta.env as ImportMetaEnv | undefined;
  if (!env) return "tmdb";
  return env.VITE_TMDB_API_KEY ? "tmdb" : "open";
}

export function usesTmdb(): boolean {
  return rowSupplier() === "tmdb";
}

/** Tests only: say whether the build carries a TMDB key (null goes back to the real build's). */
export function setBuildTmdbKeyForTests(key: string | undefined | null): void {
  buildKey = key;
}

/** Tests only: pick the supplier (null goes back to the default). */
export function setRowSupplierForTests(supplier: RowSupplier | null): void {
  override = supplier ?? undefined;
}
